import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, DollarSign, Share2, ArrowLeft, User } from 'lucide-react';
import axios from 'axios';

// Get the API URL from the environment or use the default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3011/api';

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch project details
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        // Fetch a project from the active projects endpoint and filter by ID
        const response = await axios.get(`${API_URL}/public/projects/active`);
        const foundProject = response.data.find(p => p._id === projectId);
        
        if (foundProject) {
          setProject(foundProject);
        } else {
          setError('Project not found');
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching project:', err);
        setError('Failed to load project details');
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  // Format date to readable string
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const handleDonate = () => {
    navigate(`/projects/${projectId}/donate`);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="h-64 bg-gray-200 rounded mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="h-10 bg-gray-200 rounded"></div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
            <div className="h-32 bg-gray-200 rounded mb-6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-700">{error || 'Project not found'}</p>
                <p className="mt-2">
                  <Link to="/projects" className="text-red-700 font-medium underline">
                    Return to projects
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <Link to="/projects" className="inline-flex items-center text-red-600 hover:text-red-800 mb-6">
          <ArrowLeft size={16} className="mr-2" />
          Back to projects
        </Link>
        
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-8">
          {/* Project Image */}
          <div className="relative h-80 w-full">
            <img
              src={project.image || 'https://via.placeholder.com/1200x600?text=Campaign+Image'}
              alt={project.title}
              className="w-full h-full object-cover"
            />
            {project.goalType === 'Fundraising' && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                <div className="flex justify-between items-center text-white">
                  <div>
                    <p className="text-lg font-bold">₹{project.raisedAmount.toLocaleString()}</p>
                    <p className="text-sm">raised of ₹{project.goalAmount.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">{Math.round((project.raisedAmount / project.goalAmount) * 100)}%</p>
                    <p className="text-sm">funded</p>
                  </div>
                </div>
                <div className="w-full bg-gray-300 bg-opacity-50 rounded-full h-2 mt-2">
                  <div 
                    className="bg-red-600 h-2 rounded-full" 
                    style={{ width: `${Math.min(100, (project.raisedAmount / project.goalAmount) * 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
          
          {/* Project Content */}
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h1 className="text-3xl font-bold text-gray-900">{project.title}</h1>
              <button 
                className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
                title="Share project"
              >
                <Share2 size={20} />
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="flex flex-col">
                <div className="flex items-center text-gray-600 mb-1">
                  <Calendar size={18} className="mr-2 text-red-500" />
                  <span className="text-sm font-medium">Start Date</span>
                </div>
                <span className="text-gray-900">{formatDate(project.startDate)}</span>
              </div>
              
              <div className="flex flex-col">
                <div className="flex items-center text-gray-600 mb-1">
                  <MapPin size={18} className="mr-2 text-red-500" />
                  <span className="text-sm font-medium">Location</span>
                </div>
                <span className="text-gray-900">{project.location}</span>
              </div>
              
              <div className="flex flex-col">
                <div className="flex items-center text-gray-600 mb-1">
                  <Users size={18} className="mr-2 text-red-500" />
                  <span className="text-sm font-medium">Beneficiaries</span>
                </div>
                <span className="text-gray-900">{project.beneficiaries}</span>
              </div>
              
              {project.goalType === 'Fundraising' && (
                <div className="flex flex-col">
                  <div className="flex items-center text-gray-600 mb-1">
                    <DollarSign size={18} className="mr-2 text-red-500" />
                    <span className="text-sm font-medium">Goal Amount</span>
                  </div>
                  <span className="text-gray-900">₹{project.goalAmount.toLocaleString()}</span>
                </div>
              )}
            </div>
            
            {/* Project Description */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">About this project</h2>
              <div className="prose max-w-none text-gray-700">
                <p>{project.description}</p>
                
                {project.longDescription && (
                  <div className="mt-4">
                    {project.longDescription.split('\n').map((paragraph, index) => (
                      <p key={index} className="mb-4">{paragraph}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Donation CTA */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Support this project</h3>
              <p className="text-gray-700 mb-4">
                Your contribution will help make a difference. Every donation counts towards achieving our goal.
              </p>
              <button
                onClick={handleDonate}
                className="w-full bg-red-600 text-white py-3 px-4 rounded-md font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Donate Now
              </button>
            </div>
            
            {/* NGO Information */}
            {project.ngo && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Organized by</h3>
                <div className="flex items-center">
                  {project.ngo.logo ? (
                    <img
                      src={project.ngo.logo}
                      alt={project.ngo.name}
                      className="h-16 w-16 rounded-full object-cover mr-4"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                      <User size={24} className="text-gray-500" />
                    </div>
                  )}
                  <div>
                    <h4 className="text-lg font-medium text-gray-900">{project.ngo.name}</h4>
                    <p className="text-gray-600">Verified NGO</p>
                    {/* Add more NGO details if available */}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailPage;
